import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";

import './VenueEvent.scss';

export const AdministratorVenueEventBlockedSecondaryTicketingWebsites = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const defaultSecondaryTicketingWebsite = {
        secondaryTicketingWebsiteId: "",
        name: "Select Website",
    };

    const [venueEventBlockedSecondaryTicketingWebsites, setVenueEventBlockedSecondaryTicketingWebsites] = useState([]);
    const [areVenueEventBlockedSecondaryTicketingWebsitesLoading, setAreVenueEventBlockedSecondaryTicketingWebsitesLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [selectedSecondaryTicketingWebsite, setSelectedSecondaryTicketingWebsite] = useState(defaultSecondaryTicketingWebsite);
    const [secondaryTicketingWebsites, setSecondaryTicketingWebsites] = useState(undefined);

    const getVenueEventBlockedSecondaryTicketingWebsites = () => {
        setAreVenueEventBlockedSecondaryTicketingWebsitesLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
        };

        fetch(`/api/administrator/venueeventblockedsecondaryticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setVenueEventBlockedSecondaryTicketingWebsites(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setLoadingError(error.message);
            })
            .finally(() => {
                setAreVenueEventBlockedSecondaryTicketingWebsitesLoading(false);
            });
    }

    const deleteVenueEventBlockedSecondaryTicketingWebsite = (venueEventBlockedSecondaryTicketingWebsiteKey) => {
        setAreVenueEventBlockedSecondaryTicketingWebsitesLoading(true);

        const dataItem = {
            venueEventBlockedSecondaryTicketingWebsiteKey: venueEventBlockedSecondaryTicketingWebsiteKey
        }

        fetch("/api/administrator/venueeventblockedsecondaryticketingwebsite",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenueEventBlockedSecondaryTicketingWebsites();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventBlockedSecondaryTicketingWebsitesLoading(false);
            });
    }

    const blockSecondaryTicketingWebsite = () => {
        const secondaryTicketingWebsiteId = selectedSecondaryTicketingWebsite.secondaryTicketingWebsiteId;

        if (secondaryTicketingWebsiteId === "") {
            addNotificationWithTimeout(dispatch, "error", "You must select a website");
            return;
        }

        setAreVenueEventBlockedSecondaryTicketingWebsitesLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
            secondaryTicketingWebsiteId: secondaryTicketingWebsiteId,
        }

        fetch("/api/administrator/venueeventblockedsecondaryticketingwebsite",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Blocked website for event");

                getVenueEventBlockedSecondaryTicketingWebsites();

                setSelectedSecondaryTicketingWebsite(defaultSecondaryTicketingWebsite);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventBlockedSecondaryTicketingWebsitesLoading(false);
            });
    }

    const discard = (dataItem) => {
        const newVenueEventBlockedSecondaryTicketingWebsites = [...venueEventBlockedSecondaryTicketingWebsites];
        newVenueEventBlockedSecondaryTicketingWebsites.splice(0, 1);
        setVenueEventBlockedSecondaryTicketingWebsites(newVenueEventBlockedSecondaryTicketingWebsites);
    };

    const remove = (dataItem) => {
        deleteVenueEventBlockedSecondaryTicketingWebsite(dataItem.venueEventBlockedSecondaryTicketingWebsiteKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="venueEventBlockedSecondaryTicketingWebsiteKey"
        />
    );

    const getSecondaryTicketingWebsites = () => {
        fetch(`/api/administrator/secondaryticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allSecondaryTicketingWebsites = [defaultSecondaryTicketingWebsite].concat(result);
                setSecondaryTicketingWebsites(allSecondaryTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const secondaryTicketingWebsiteChanged = (event) => {
        setSelectedSecondaryTicketingWebsite(event.target.value);
    };

    useEffect(() => {
        if (props.venueEventKey) {
            getSecondaryTicketingWebsites();
            getVenueEventBlockedSecondaryTicketingWebsites();
        }
    }, []);

    return (
        <>
            {areVenueEventBlockedSecondaryTicketingWebsitesLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <>
                    <div className="row mb-3 mt-3">
                        <div className="col-md-6 col-lg-8">
                            <DropDownList
                                id="secondaryTicketingWebsite"
                                textField="name"
                                dataItemKey="secondaryTicketingWebsiteId"
                                data={secondaryTicketingWebsites}
                                value={selectedSecondaryTicketingWebsite}
                                onChange={secondaryTicketingWebsiteChanged}
                            />
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <Button
                                themeColor={"primary"}
                                type={"button"}
                                onClick={blockSecondaryTicketingWebsite}
                            >
                                Block Website
                            </Button>
                        </div>
                    </div>

                    <Grid
                        data={venueEventBlockedSecondaryTicketingWebsites}
                        editField={editField}
                        dataItemKey={"venueEventBlockedSecondaryTicketingWebsiteKey"}
                        resizable={true}
                        scrollable="none"
                    >
                        <Column field="secondaryTicketingWebsiteName" title={"Website"} />
                        <Column cell={MyCommandCell} width="150px" />
                    </Grid>
                </>
            ) : <></>)
            }
        </>
    );
}